import React, { ReactNode, ErrorInfo } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Home } from './pages/Home';
import { Workout } from './pages/Workout';
import { History } from './pages/History';
import { Calendar } from './pages/Calendar';
import { Login } from './pages/Login';
import { ProfileSetup } from './pages/ProfileSetup';
import { Leaderboard } from './pages/Leaderboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// --- STANDARD ERROR BOUNDARY ---

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// FIX: Using `React.Component` directly ensures correct type resolution for the class component, which resolves the issue of 'props' not being found.
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // FIX: Use class property syntax for state initialization. The existing constructor was causing type resolution issues for `this.state` and `this.props`.
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Application Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
        return (
          <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center">
             <div className="w-16 h-16 bg-white rounded-sm flex items-center justify-center text-black font-black text-3xl mb-6">
              L
            </div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tighter mb-2">Application Error</h1>
            <p className="text-slate-400 font-mono text-xs uppercase tracking-widest mb-8">The application encountered an unexpected problem.</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-white text-black font-bold uppercase py-3 px-6 hover:bg-gray-200 transition-colors tracking-widest text-xs"
            >
              Reload Application
            </button>
          </div>
        );
    }

    return this.props.children;
  }
}

// --- APP COMPONENTS ---

const RequireAuthWithoutProfileCheck = ({ children }: { children?: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const PrivateRoute = ({ children }: { children?: ReactNode }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (!profile && user) return <Navigate to="/profile-setup" />;

  return <>{children}</>;
};

const Layout = ({ children }: { children?: ReactNode }) => {
  return (
    <div className="min-h-screen bg-dark text-white font-sans selection:bg-primary selection:text-dark">
      <Header />
      <main>{children}</main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            
            <Route path="/profile-setup" element={
              <RequireAuthWithoutProfileCheck>
                <ProfileSetup />
              </RequireAuthWithoutProfileCheck>
            } />

            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout>
                    <Home />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/workout/:type"
              element={
                <PrivateRoute>
                  <Layout>
                    <Workout />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/history"
              element={
                <PrivateRoute>
                  <Layout>
                    <History />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <Layout>
                    <Calendar />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Leaderboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
